import { ReactElement, useCallback, useEffect, useState } from 'react';
import { useStore } from '../../lib/hooks/useStore';
import { FieldType } from '../../lib/typegen/field-type';
import { Form } from '../../lib/typegen/form';
import { KeyValue } from '../../lib/typegen/key-value';
import GenericButton from '../button-generic';
import style from './style.module.scss';
import { AppIconType } from '../../lib/typegen/app-icon-type';
import logger from '../../lib/helpers/logger';
import { store } from '../../lib/stores/store';

interface PayorInformationFormProps {
	form?: Form | null;
	handleNavigation: (pageId: string, params?: KeyValue[]) => void;
}

type PayorType = 'residential' | 'commercial';

export default function PayorInformationForm({ form, handleNavigation, ...props }: PayorInformationFormProps) {
	const {
		configStore: { getString },
		uiStore: { setBackEnabled, setCustomBackAction, clearCustomBackAction },
	} = useStore();

	const [currentStep, setCurrentStep] = useState(1);
	const [isStepValid, setIsStepValid] = useState(false);
	const [payorType, setPayorType] = useState<PayorType>('residential');
	const [formData, setFormData] = useState({
		firstName: store.accountStore.payorInformation?.firstName || '',
		lastName: store.accountStore.payorInformation?.lastName || '',
		street: store.accountStore.payorInformation?.street || '',
		city: store.accountStore.payorInformation?.city || '',
		state: store.accountStore.payorInformation?.state || '',
		zip: store.accountStore.payorInformation?.zip?.toString() || '',
		phone: store.accountStore.payorInformation?.phoneNumber?.toString() || '',
		email: store.accountStore.payorInformation?.emailAddress || '',
	});

	// Validation functions
	const isValidEmail = (email: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailRegex.test(email);
	};

	const isValidPhone = (phone: string): boolean => {
		const digitsOnly = phone.replace(/\D/g, '');
		return digitsOnly.length === 10;
	};

	const isValidZip = (zip: string): boolean => {
		const digitsOnly = zip.replace(/\D/g, '');
		return digitsOnly.length === 5;
	};

	const isValidState = (state: string): boolean => {
		const trimmedState = state.trim();
		return trimmedState.length === 2 && /^[A-Za-z]{2}$/.test(trimmedState);
	};

	// Formatting functions
	const formatPhoneNumber = (value: string): string => {
		const digitsOnly = value.replace(/\D/g, '');
		if (digitsOnly.length <= 3) return digitsOnly;
		if (digitsOnly.length <= 6) return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
		return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
	};

	const formatZipCode = (value: string): string => {
		return value.replace(/\D/g, '').slice(0, 5);
	};

	const formatState = (value: string): string => {
		return value
			.replace(/[^A-Za-z]/g, '')
			.slice(0, 2)
			.toUpperCase();
	};

	// Get raw digits only for storage
	const getPhoneDigits = (formattedPhone: string): string => {
		return formattedPhone.replace(/\D/g, '');
	};

	const handleFormSubmit = () => {
		// get payor information and store it for the payment request
		handleNavigation('payment-method');
		store.accountStore.setPayorInformation({
			firstName: formData.firstName,
			lastName: formData.lastName,
			street: formData.street,
			city: formData.city,
			state: formData.state,
			zip: Number(formData.zip.replace(/\D/g, '')),
			phoneNumber: getPhoneDigits(formData.phone),
			emailAddress: formData.email,
		});
		logger.debug.log('Payor information submitted: ', store.accountStore.payorInformation);
	};

	const handleNext = () => {
		if (currentStep < 3) {
			setCurrentStep(currentStep + 1);
		} else {
			handleFormSubmit();
		}
	};

	const handleBack = useCallback(() => {
		if (currentStep > 1) {
			setCurrentStep(currentStep - 1);
		}
	}, [currentStep]);

	useEffect(() => {
		setBackEnabled(true);

		if (currentStep > 1) {
			setCustomBackAction(handleBack);
		} else {
			clearCustomBackAction();
		}
	}, [currentStep, handleBack, setBackEnabled, setCustomBackAction, clearCustomBackAction]);

	useEffect(() => {
		let fieldsToValidate: (keyof typeof formData)[] = [];

		switch (currentStep) {
			case 1:
				fieldsToValidate = payorType === 'residential' ? ['firstName', 'lastName'] : ['firstName'];
				break;
			case 2:
				fieldsToValidate = ['street', 'city', 'state', 'zip'];
				break;
			case 3:
				fieldsToValidate = ['phone', 'email'];
				break;
		}

		const stepFieldsValid = fieldsToValidate.every((field) => {
			const value = formData[field];
			const stringValue = typeof value === 'string' ? value : String(value);
			switch (field) {
				case 'email':
					return stringValue.trim().length === 0 || isValidEmail(stringValue);
				case 'phone':
					return stringValue.trim().length === 0 || isValidPhone(stringValue);
				case 'zip':
					if (stringValue.trim().length === 0) return false;
					return isValidZip(stringValue);
				case 'state':
					if (stringValue.trim().length === 0) return false;
					return isValidState(stringValue);
				default:
					return stringValue.trim().length > 0;
			}
		});

		setIsStepValid(stepFieldsValid);
	}, [formData, currentStep, payorType]);

	const fieldTypes: Record<FieldType, 'text' | 'tel' | 'email' | 'number'> = {
		[FieldType.Email]: 'email',
		[FieldType.Tel]: 'tel',
		[FieldType.Text]: 'text',
		[FieldType.Number]: 'text',
	};

	const getFieldByName = (name: string): ReactElement => {
		const field = form?.fields?.find((e) => e.name === name);
		if (field) {
			// Map field names to formData keys
			const fieldNameMap: Record<string, keyof typeof formData> = {
				'payor-information-first-name': 'firstName',
				'payor-information-last-name': 'lastName',
				'payor-information-street': 'street',
				'payor-information-city': 'city',
				'payor-information-state': 'state',
				'payor-information-zip': 'zip',
				'payor-information-phone': 'phone',
				'payor-information-email': 'email',
			};

			const stateKey = fieldNameMap[field.name];

			const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
				let value = e.target.value;

				// Apply formatting based on field type
				if (stateKey === 'phone') {
					value = formatPhoneNumber(value);
				} else if (stateKey === 'zip') {
					value = formatZipCode(value);
				} else if (stateKey === 'state') {
					value = formatState(value);
				}

				setFormData((prev) => ({ ...prev, [stateKey]: value }));
			};

			// Determine input type - use tel for phone and zip for better mobile keyboards
			let inputType = fieldTypes[field.type] ?? 'text';
			if (stateKey === 'phone') inputType = 'tel';
			if (stateKey === 'zip') inputType = 'tel';

			return (
				<input
					className={style.fieldset__input}
					type={inputType}
					name={field.name}
					dir={stateKey === 'phone' || stateKey === 'email' || stateKey === 'zip' ? 'ltr' : undefined}
					placeholder={getString(field.label)}
					value={formData[stateKey] || ''}
					onChange={handleInputChange}
					maxLength={stateKey === 'phone' ? 14 : stateKey === 'zip' ? 5 : stateKey === 'state' ? 2 : undefined}
				/>
			);
		}
		return <></>;
	};

	const renderStep = () => {
		switch (currentStep) {
			case 1:
				return (
					<>
						<div className={style.form__radioGroup}>
							<label className={style.form__radioLabel}>
								<input
									type='radio'
									name='payor-type'
									value='residential'
									checked={payorType === 'residential'}
									onChange={(e) => setPayorType(e.target.value as PayorType)}
								/>
								{getString('payor-information-residential')}
							</label>
							<label className={style.form__radioLabel}>
								<input
									type='radio'
									name='payor-type'
									value='commercial'
									checked={payorType === 'commercial'}
									onChange={(e) => setPayorType(e.target.value as PayorType)}
								/>
								{getString('payor-information-commercial')}
							</label>
						</div>
						{payorType === 'residential' ? (
							<>
								<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>
									{getFieldByName('payor-information-first-name')}
								</fieldset>
								<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>
									{getFieldByName('payor-information-last-name')}
								</fieldset>
							</>
						) : (
							<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>
								<input
									className={style.fieldset__input}
									type='text'
									name='company-name'
									placeholder={getString('payor-information-company-name')}
									value={formData.firstName || ''}
									onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
								/>
							</fieldset>
						)}
					</>
				);
			case 2:
				return (
					<>
						<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>{getFieldByName('payor-information-street')}</fieldset>
						<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>{getFieldByName('payor-information-city')}</fieldset>
						<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>
							{getFieldByName('payor-information-state')}
							{getFieldByName('payor-information-zip')}
						</fieldset>
					</>
				);
			case 3:
				return (
					<>
						<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>{getFieldByName('payor-information-phone')}</fieldset>
						<fieldset className={[style.form__fieldset, style.fieldset].join(' ')}>{getFieldByName('payor-information-email')}</fieldset>
					</>
				);
			default:
				return null;
		}
	};

	useEffect(() => {
		handleShowKeyboard();
	}, [currentStep]);

	const handleShowKeyboard = () => {
		setTimeout(() => {
			const input = Array.from(document.getElementsByTagName('input')).filter((input) => input.type !== 'hidden')[0];
			if (input) {
				input.focus();
				KioApp.StartKeyboard('', '', input.type, false, '', '');
			} else {
				KioApp.CloseKeyboard(0);
			}
		}, 100);
	};

	return (
		<form {...props} className={style['form']} onSubmit={(e) => e.preventDefault()}>
			{renderStep()}

			<div className={style.form__buttons}>
				<GenericButton
					icon={currentStep === 3 ? AppIconType.AcceptSolid : AppIconType.CircleArrowRight}
					className={style.form__submit}
					type='button'
					innerText={currentStep === 3 ? getString('done') : getString('next')}
					isGrowHorizontally={false}
					disabled={!isStepValid}
					onClick={handleNext}
				/>
			</div>
		</form>
	);
}
